import { ChevronDownIcon, PlusIcon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import { useEffect, useId, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/Command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { Tooltip } from '@/design-system';
import { useStoredResourceTags } from '@/hooks/useStoredResourceTags/useStoredResourceTags';
import { useDatasets } from '@/hooks/useSwr';
import DatasetIcon from '@/icons/DatasetIcon';
import { GetDatasetsQueryParams } from '@/types/schema';
import { cn } from '@/utils/tailwind';
import { isUUID } from '@/utils/uuid';

import { ShowAllPickerExtraItem } from './ResourceTagFilter/ShowAllPickerExtraItem';

const DatasetNameWithTooltip = ({ name }: { name?: string }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, [name]);

  return (
    <Tooltip title={isTruncated ? name : undefined}>
      <span className="flex-grow truncate whitespace-nowrap" ref={textRef}>
        {name}
      </span>
    </Tooltip>
  );
};

export function DatasetPickerV2(props: {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  filter?: GetDatasetsQueryParams;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const { selectedTagIds } = useStoredResourceTags();

  const id = useId();
  const [debouncedSearch] = useDebounce(search, 300, { leading: true });

  const datasets = useDatasets(
    open
      ? {
          ...props.filter,
          ...{ name_contains: debouncedSearch },
          tag_value_id: showAll ? undefined : selectedTagIds,
          exclude_corrections_datasets: true,
        }
      : null,
    undefined,
    { keepPreviousData: true }
  );

  const datasetsByNameOrId = useDatasets(
    props.value != null
      ? isUUID(props.value)
        ? { id: [props.value] }
        : { name: props.value }
      : null,
    undefined,
    { revalidateOnFocus: false }
  );

  const filtered = datasets.data?.filter((item) => {
    if (search === '') return true;
    const haystack = item.name.trim().toLowerCase();
    const needle = search.trim().toLowerCase();
    return haystack.includes(needle);
  });

  const exactSearchFind = !!datasets.data?.find((item) => item.name === search);

  const label =
    datasets.data?.find((item) => item.id === props.value)?.name ??
    datasetsByNameOrId.data?.find(
      (item) => item.id === props.value || item.name === props.value
    )?.name ??
    props.value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          className={cn(
            'flex min-w-0 flex-shrink items-center gap-2 rounded-md border border-secondary p-1.5 px-2.5 transition-colors hover:bg-secondary-hover active:bg-secondary',
            props.size === 'sm' && 'min-h-8 gap-1.5 p-0.5 pl-2 pr-1.5 text-sm',
            props.className
          )}
        >
          <DatasetIcon className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-shrink grow items-center gap-2 truncate whitespace-nowrap text-left">
            {label == null ? 'Select dataset...' : label}
          </span>
          <ChevronDownIcon
            className={cn(
              'mr-0.5 h-5 w-5 shrink-0 transition-all',
              open && 'rotate-180'
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-[50vh] min-w-[400px] overflow-auto p-0"
      >
        {datasets.isLoading && (
          <div className="pointer-events-none absolute inset-x-0 top-0">
            <LinearProgress />
          </div>
        )}
        <Command shouldFilter={false}>
          <CommandInput
            className="m-2 mb-0 px-2 py-3"
            value={search}
            onValueChange={setSearch}
            placeholder="Select dataset..."
          />

          <CommandList>
            <CommandGroup>
              {filtered?.map((dataset) => (
                <CommandItem
                  key={dataset.id}
                  onSelect={() => {
                    props.onChange(dataset.id);
                    setOpen(false);
                  }}
                  className="flex items-center gap-2"
                >
                  <DatasetIcon className="size-4 shrink-0" />

                  <DatasetNameWithTooltip name={dataset.name} />
                  <span className="flex-shrink-0 whitespace-nowrap text-xs text-tertiary">
                    {dataset.example_count === 1
                      ? '1 example'
                      : `${dataset.example_count} examples`}
                  </span>
                </CommandItem>
              ))}

              {search && !exactSearchFind ? (
                <CommandItem
                  key={search}
                  onSelect={() => {
                    props.onChange(search);
                    setOpen(false);
                  }}
                  className="flex items-center gap-2"
                >
                  <PlusIcon className="size-4 shrink-0" />

                  {search}
                </CommandItem>
              ) : null}

              <ShowAllPickerExtraItem
                showAll={showAll}
                resources={datasets.data}
                setShowAll={setShowAll}
                hasSelectedTags={selectedTagIds.length > 0}
                className="justify-between py-1.5 pl-2"
              />
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
